import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { OfferInputs, OfferResults, ScenarioPct } from "../../types/ambassadors";
import { count, handle, pct, plural, usd, yearsLabel } from "./format";
import { initials } from "./photo";
import "./Presentation.css";

// Present mode: the offer as the DJ sees it in the meeting — one white,
// screenshot-ready page sized to a 16:9 screen, printable to PDF. It shows only
// what the DJ is offered and why. Never the private settings: no CPMs, no
// multipliers, no taper weights, no notes. Rendered on <body> (outside the
// admin layout) so it covers the whole window and prints on its own.

type N = number | null;

const WORDS = ["zero", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten"];
const SCENARIOS: { key: keyof ScenarioPct; label: string }[] = [
  { key: "conservative", label: "Conservative" },
  { key: "base", label: "Base case" },
  { key: "upside", label: "Upside" },
];

// "2 Reels + 2 Stories"; a kind with no posts is left out.
function postsLine(reels: N, stories: N): string {
  const parts = [reels ? plural(reels, "Reel") : "", stories ? plural(stories, "Story", "Stories") : ""].filter(Boolean);
  return parts.length ? parts.join(" + ") : "No posts";
}

function scenarioLine(key: keyof ScenarioPct, pctOfForecast: N): string {
  if (key === "base" && pctOfForecast === 100) return "On Request’s current forecast";
  return `If revenue is ${count(pctOfForecast)}% of forecast`;
}

interface Props {
  inputs: OfferInputs;
  results: OfferResults;
  onClose: () => void;
}

export default function OfferPresentation({ inputs, results: r, onClose }: Props) {
  const back = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    back.current?.focus();
    // While it's open, printing prints this page alone (Presentation.css).
    document.body.classList.add("amb-presenting");
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.classList.remove("amb-presenting");
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const years = inputs.years;
  const yearsText = years === 1 ? "year" : "years";
  const name = inputs.djName.trim();
  const who = [handle(inputs.instagramHandle), inputs.followers !== null ? `${count(inputs.followers)} followers` : ""].filter(Boolean);

  return createPortal(
    <div className="amb-present-root" role="dialog" aria-modal="true" aria-label={`${name} × Request proposal`}>
      <div className="amb-present">
        <header className="amb-p-top">
          <img className="amb-p-logo" src="/request-wordmark.png" alt="Request" />
          <span className="amb-p-kicker">Founding Ambassador Proposal</span>
          <div className="amb-p-actions">
            <button ref={back} type="button" onClick={onClose}>
              ← Back to calculator
            </button>
            <button type="button" className="amb-p-primary" onClick={() => window.print()}>
              Print / Save as PDF
            </button>
          </div>
        </header>

        <main className="amb-p-grid">
          <section className="amb-p-left">
            <div className="amb-p-head">
              <h1 className="amb-p-title">
                <span>YOUR INFLUENCE.</span>
                <span>OUR GROWTH.</span>
                <span className="amb-p-green">SHARED SUCCESS.</span>
              </h1>
              <p className="amb-p-sub">{name} × Request</p>
              <p className="amb-p-term">{years}-Year Founding Partnership</p>
            </div>

            <div className="amb-p-dj">
              <span className="amb-p-avatar">{inputs.photo ? <img src={inputs.photo} alt="" /> : initials(name)}</span>
              <span className="amb-p-dj-text">
                <b>{name}</b>
                {who.length > 0 && <span>{who.join(" · ")}</span>}
              </span>
            </div>

            <div className="amb-p-commit">
              <span className="amb-p-label">Your commitment</span>
              <b>{postsLine(inputs.reelsPerQuarter, inputs.storiesPerQuarter)} every quarter</b>
              <span>
                {postsLine(r.reelsPerYear, r.storiesPerYear)} a year · {postsLine(r.totalReels, r.totalStories)} over {years} {yearsText}
              </span>
            </div>

            <div className="amb-p-why">
              <p>
                Rather than paying you exclusively for sponsored posts, Request offers you the opportunity to earn a percentage of
                platform revenue over {WORDS[years] ?? years} {yearsText}.
              </p>
              <p className="amb-p-why-strong">As Request grows, your potential earnings grow.</p>
            </div>
          </section>

          <section className="amb-p-right">
            <div className="amb-p-values">
              <div className="amb-p-card">
                <span className="amb-p-label">Estimated annual promotional value</span>
                <b>{usd(r.annualPartnershipValue)}</b>
                <span>per year</span>
              </div>
              <div className="amb-p-card">
                <span className="amb-p-label">Partnership market value</span>
                <b>{usd(r.partnershipMarketValue)}</b>
                <span>
                  over {years} {yearsText}
                </span>
              </div>
            </div>

            <div className="amb-p-block">
              <span className="amb-p-label">Your share of Request platform revenue</span>
              <div className="amb-p-shares">
                {r.periods.map((p) => (
                  <div key={p.start} className="amb-p-share">
                    <span>{yearsLabel(p.start, p.end)}</span>
                    <b>{pct(p.ratePct)}</b>
                  </div>
                ))}
              </div>
            </div>

            <div className="amb-p-block">
              <span className="amb-p-label">
                Projected earnings over {years} {yearsText}
              </span>
              <div className="amb-p-earnings">
                {SCENARIOS.map((s) => (
                  <div key={s.key} className={`amb-p-card amb-p-earn${s.key === "base" ? " amb-p-earn-base" : ""}`}>
                    <span className="amb-p-label">{s.label}</span>
                    <b>{usd(r.totals[s.key])}</b>
                    <span>{scenarioLine(s.key, inputs.scenarioPct[s.key])}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="amb-p-tablewrap">
            <table className="amb-p-table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Platform revenue (base case)</th>
                  <th>Your share</th>
                  <th>Conservative</th>
                  <th className="amb-p-basecol">Base case</th>
                  <th>Upside</th>
                </tr>
              </thead>
              <tbody>
                {r.rows.map((row) => (
                  <tr key={row.year}>
                    <td>Year {row.year}</td>
                    <td>{usd(row.baseRevenue)}</td>
                    <td>{pct(row.ratePct)}</td>
                    <td>{usd(row.conservative)}</td>
                    <td className="amb-p-basecol">{usd(row.base)}</td>
                    <td>{usd(row.upside)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td />
                  <td />
                  <td>{usd(r.totals.conservative)}</td>
                  <td className="amb-p-basecol">{usd(r.totals.base)}</td>
                  <td>{usd(r.totals.upside)}</td>
                </tr>
              </tfoot>
            </table>
            </div>

            <p className="amb-p-disclaimer">
              Projections only — not guaranteed earnings and not a binding offer. Payments depend on Request’s actual future platform
              revenue (the fees Request keeps, not total request volume). Figures are rounded to the nearest dollar.
            </p>
          </section>
        </main>
      </div>
    </div>,
    document.body
  );
}
